import { BetrokkenTreinDto, BetrokkenTreinMaterieelDto, BusEvacuatieDto } from "@/shared/types";
import { Uitvoerder, UitvoerderDisplay } from "./constants";

export interface BetrokkenTrein {
    id: number;
    type: "Betrokken" | "Gestrand" | "Handmatig";
    typeLabel: string;
    treinNummer: number;
    isRelevant: boolean; // true when this record should be shown to regievoerder in the overview ((type = betrokken | type = gestrand and manually selected by regievoerder) | type = handmatig)
    treinType: string;
    handelingsVrij?: boolean;
    materieel?: BetrokkenMaterieel;
    aantalReizigers?: number;
    busEvacuatie?: BusEvacuatie;
    treinEvacuatie?: TreinEvacuatie;
}

export const BetrokkenTrein = {
    fromDto: (dto: BetrokkenTreinDto): BetrokkenTrein => ({
        id: dto.id,
        type: dto.type,
        isRelevant: dto.isRelevant,
        typeLabel: dto.type === "Betrokken" ? "Betrokken trein" : dto.type === "Gestrand" ? "Gestrande trein" : "Handmatige trein",
        treinNummer: dto.treinNummer,
        treinType: dto.treinType,
        handelingsVrij: dto.handelingsVrij,
        materieel: BetrokkenMaterieel.fromDto(dto.materieel),
        aantalReizigers: dto.schattingReizigers,
        busEvacuatie: BusEvacuatie.fromDto(dto.busEvacuatie),
        treinEvacuatie: TreinEvacuatie.fromDto(dto.treinEvacuatie),
    }),
    sort: (a: BetrokkenTrein, b: BetrokkenTrein) => {
        if (a.type === "Betrokken" && b.type !== "Betrokken") return -1;
        if (a.type !== "Betrokken" && b.type === "Betrokken") return 1;
        return a.id - b.id;
    },
    formatTrainLabel: (trein: Pick<BetrokkenTrein, "treinType" | "treinNummer">) => {
        return `${trein.treinType}${trein.treinNummer}`;
    },
};

type BetrokkenMaterieel = {
    aanduiding: string;
    aantalBakken: number;
};

const BetrokkenMaterieel = {
    fromDto: (dto: BetrokkenTreinMaterieelDto | undefined) => {
        if (!dto) return undefined;
        return {
            aanduiding: dto.aanduiding,
            aantalBakken: dto.aantalBakken,
        };
    },
};

export type BusEvacuatieStatus = "Aanvraag" | "Gedeeltelijk" | "Volledig" | "AnnuleringAangevraagd" | "Geannuleerd" | "Afgerond";

export type BusEvacuatie = {
    id: number;
    aantalBussen: number;
    opstelLocatie: string;
    status: BusEvacuatieStatus;
    uitvoerder: Uitvoerder;
    etas: Date[];
    afzetStation: string;
    etaAfzetStation?: string | undefined;
};

export const BusEvacuatie = {
    fromDto: (dto: BusEvacuatieDto | undefined): BusEvacuatie | undefined => {
        if (!dto) return undefined;
        return {
            id: dto.id,
            aantalBussen: dto.aantalBussen,
            afzetStation: dto.naarStation,
            etaAfzetStation: dto.etaNaarStation,
            opstelLocatie: dto.opstelLocatie,
            status: dto.status,
            uitvoerder: dto.uitvoerder as BusEvacuatie["uitvoerder"],
            etas: (dto.etas?.map((eta) => new Date(eta)) ?? []).sort((a, b) => a.getTime() - b.getTime()),
        };
    },

    hasActionsAvailable: ({ status }: Pick<BusEvacuatie, "status">) => {
        return status === "Aanvraag" || status === "Gedeeltelijk" || status === "Volledig" || status === "Afgerond";
    },

    isCancellable: ({ status }: Pick<BusEvacuatie, "status">) => {
        return status === "Aanvraag" || status === "Gedeeltelijk" || status === "Volledig";
    },

    hasNoEtasOrIsCompleted: ({ status, etas }: Pick<BusEvacuatie, "status" | "etas">) => {
        return status === "Afgerond" || status === "AnnuleringAangevraagd" || status === "Geannuleerd" || !etas?.length;
    },

    formatUitvoerder: (evacuatie: Pick<BusEvacuatie, "uitvoerder">) => {
        return UitvoerderDisplay[evacuatie.uitvoerder];
    },
};

export type TreinEvacuatie = {
    status: "Afgerond" | "Geannuleerd";
    vertrekTijd: string;
    afzetStations: string[];
    toelichting?: string;
};

export const TreinEvacuatie = {
    fromDto: (treinEvacuatie: BetrokkenTreinDto["treinEvacuatie"]): TreinEvacuatie | undefined => {
        if (!treinEvacuatie) return undefined;
        return {
            status: treinEvacuatie.status,
            vertrekTijd: treinEvacuatie.vertrekTijd,
            afzetStations: treinEvacuatie.afzetStations,
            toelichting: treinEvacuatie.toelichting,
        };
    },
};
